export interface AccessTokenClaims {
  readonly sub?: string
  readonly exp?: number
  readonly aud?: string | readonly string[]
  readonly azp?: string
  readonly iss?: string
  readonly email?: string
  readonly name?: string
  readonly tenant_id?: string
  readonly realm_access?: { readonly roles?: readonly string[] }
}

/**
 * Decodifica o payload do JWT SEM verificar assinatura.
 *
 * ⚠️ SÓ para UI e diagnóstico. A autoridade é o backend (RS256 via JWKS,
 * app/core/auth.py). Nada aqui decide segurança — decide o que MOSTRAR.
 */
export function decodeAccessToken(token: string): AccessTokenClaims | null {
  const part = token.split('.')[1]
  if (part === undefined || part === '') return null
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, '=')
    const bin = atob(padded)
    // TextDecoder, não escape/unescape: nomes em pt-BR têm acento (UTF-8 multibyte)
    // e atob devolve bytes latin1 — "José" viraria "JosÃ©".
    const bytes = Uint8Array.from(bin, (ch) => ch.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes)) as AccessTokenClaims
  } catch {
    return null
  }
}

export type AuthProblemCode =
  'undecodable' | 'missing_tenant_claim' | 'invalid_tenant_claim' | 'audience_mismatch' | 'no_roles'

export interface AuthProblem {
  readonly code: AuthProblemCode
  readonly detail: string
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/**
 * Reproduz as guardas de app/core/auth.py ANTES do primeiro request.
 *
 * Por que isto existe: sem os mappers certos no realm, TODA chamada devolve 401
 * com code `invalid_token` — o MESMO código de um token corrompido
 * (auth.py:184 e auth.py:178 usam o mesmo). Só a `message` em pt-BR distingue.
 * O usuário veria "Token inválido" e ninguém saberia que falta um mapper.
 *
 * Aqui o problema aparece nomeado, com o passo a passo da correção.
 *
 * @param apiAudience o que o BACKEND exige em `aud` (VITE_API_AUDIENCE =
 *   "liberty-finance", o resource server) — NÃO o client do login
 *   (VITE_KEYCLOAK_CLIENT_ID = "liberty-finance-front"). São coisas diferentes;
 *   a ponte entre elas é o mapper Audience.
 */
export function preflightClaims(
  claims: AccessTokenClaims | null,
  apiAudience: string,
  loginClientId: string,
): AuthProblem[] {
  if (!claims) {
    return [{ code: 'undecodable', detail: 'O access token não é um JWT decodificável.' }]
  }

  const problems: AuthProblem[] = []

  if (claims.tenant_id === undefined || claims.tenant_id === '') {
    problems.push({
      code: 'missing_tenant_claim',
      detail:
        `O access token não traz o claim "tenant_id", e a API rejeita com 401 sem ele.\n\n` +
        `No Keycloak: Clients → ${loginClientId} → Client scopes → ` +
        `${loginClientId}-dedicated → Add mapper → By configuration → User Attribute:\n` +
        `  Name = tenant_id\n  User Attribute = tenant_id\n  Token Claim Name = tenant_id\n` +
        `  Claim JSON Type = String\n  Add to access token = ON\n\n` +
        `E preencha o atributo "tenant_id" (UUID do tenant) em Users → seu usuário → Attributes.`,
    })
  } else if (!UUID_RE.test(claims.tenant_id)) {
    problems.push({
      code: 'invalid_tenant_claim',
      detail: `O claim tenant_id = "${claims.tenant_id}" não é um UUID válido.`,
    })
  }

  const aud = claims.aud === undefined ? [] : Array.isArray(claims.aud) ? claims.aud : [claims.aud]
  if (!aud.includes(apiAudience)) {
    problems.push({
      code: 'audience_mismatch',
      detail:
        `O claim "aud" (${aud.length > 0 ? aud.join(', ') : 'ausente'}) não contém ` +
        `"${apiAudience}". A API valida aud INCONDICIONALMENTE, então toda chamada daria 401.\n\n` +
        `O login acontece via "${loginClientId}", mas o backend é o resource server ` +
        `"${apiAudience}" — quem liga os dois é um mapper de Audience:\n` +
        `  Clients → ${loginClientId} → Client scopes → ${loginClientId}-dedicated →\n` +
        `  Add mapper → By configuration → Audience:\n` +
        `    Included Client Audience = ${apiAudience}\n    Add to access token = ON`,
    })
  }

  if (!claims.realm_access?.roles?.length) {
    problems.push({
      code: 'no_roles',
      detail:
        'O token não traz realm_access.roles. As consultas funcionam, mas toda ' +
        'gravação retornará 403. Atribua finance_writer (ou finance_admin) ao usuário ' +
        'em Users → seu usuário → Role mapping.',
    })
  }

  return problems
}
